/**
 * The guided calibration sequence ("write each character twice"), as pure
 * state so the panel stays thin and the flow is testable.
 *
 * Samples are collected here and only handed to the recogniser when the
 * player finishes (or saves early), so "Back" can redo an earlier sample —
 * the recogniser has no way to remove a single sample once it is added.
 */
import type { HandwritingRecognizer, HwChar, HwStroke } from '../contracts.ts';
import { parseSamples, serializeSamples } from './recognizer/samples.ts';
import { HW_CHARS } from './recognizer/templates.ts';

export const SAMPLES_PER_CHAR = 2;

export interface CalibrationStep {
  char: HwChar;
  /** 0-based: which of the SAMPLES_PER_CHAR samples of this character. */
  repeat: number;
}

export const CALIBRATION_STEPS: readonly CalibrationStep[] = HW_CHARS.flatMap((char) =>
  Array.from({ length: SAMPLES_PER_CHAR }, (_, repeat) => ({ char, repeat })),
);

export interface CalibrationState {
  /** Current step; equal to CALIBRATION_STEPS.length when every step is done. */
  index: number;
  /** Per step: the strokes written, or null when skipped / not yet written. */
  samples: readonly (HwStroke[] | null)[];
}

export const initialCalibration = (): CalibrationState => ({ index: 0, samples: CALIBRATION_STEPS.map(() => null) });

export const currentStep = (s: CalibrationState): CalibrationStep | null => CALIBRATION_STEPS[s.index] ?? null;

export const isComplete = (s: CalibrationState): boolean => s.index >= CALIBRATION_STEPS.length;

function setAndAdvance(s: CalibrationState, sample: HwStroke[] | null): CalibrationState {
  if (isComplete(s)) return s;
  const samples = s.samples.slice();
  samples[s.index] = sample;
  return { index: s.index + 1, samples };
}

/** Store the strokes for the current step and move on (empty ink is ignored). */
export const recordSample = (s: CalibrationState, strokes: readonly HwStroke[]): CalibrationState => {
  const ink = strokes.filter((st) => st.length > 0);
  return ink.length ? setAndAdvance(s, ink) : s;
};

/** Leave the current step without a sample. */
export const skipStep = (s: CalibrationState): CalibrationState => setAndAdvance(s, null);

/** Go back one step to write it again (its old sample is discarded). */
export function stepBack(s: CalibrationState): CalibrationState {
  if (s.index <= 0) return s;
  const index = s.index - 1;
  const samples = s.samples.slice();
  samples[index] = null;
  return { index, samples };
}

/** Steps finished (written or skipped) out of the total, for the progress bar. */
export const progressOf = (s: CalibrationState): { done: number; total: number } => ({
  done: Math.min(s.index, CALIBRATION_STEPS.length),
  total: CALIBRATION_STEPS.length,
});

/** Every sample written so far, in step order. */
export function collectedSamples(s: CalibrationState): { char: HwChar; strokes: HwStroke[] }[] {
  const out: { char: HwChar; strokes: HwStroke[] }[] = [];
  s.samples.forEach((strokes, i) => {
    const step = CALIBRATION_STEPS[i];
    if (strokes && step) out.push({ char: step.char, strokes });
  });
  return out;
}

/**
 * Hand the collected samples to the recogniser. A fresh sample of a character
 * *replaces* that character's older samples (re-calibrating is how a player
 * fixes a bad earlier session); characters skipped this time keep theirs.
 *
 * Per-character replacement goes through export → filter → import, because
 * the recogniser contract has no "remove one character" call. If the export
 * isn't in our format (a different recogniser was plugged in), the samples
 * are simply added.
 *
 * @returns the number of samples added
 */
export function applyCalibration(
  recognizer: HandwritingRecognizer,
  samples: readonly { char: HwChar; strokes: HwStroke[] }[],
): number {
  if (!samples.length) return 0;
  const rewritten = new Set(samples.map((s) => s.char));
  try {
    const kept = parseSamples(recognizer.exportUserSamples()).filter((s) => !rewritten.has(s.char));
    recognizer.importUserSamples(serializeSamples(kept));
  } catch {
    // Foreign or unreadable export: keep everything and just add.
  }
  for (const s of samples) recognizer.addUserSample(s.char, s.strokes);
  return samples.length;
}
